/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, {
	forwardRef,
	type Ref,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { bindAll } from 'bind-event-listener';
import { defineMessages, useIntl } from 'react-intl-next';

import { getDocument } from '@atlaskit/browser-apis';
import { cssMap, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { ExitingPersistence, FadeIn } from '@atlaskit/motion';
import { fg } from '@atlaskit/platform-feature-flags';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import type { ConversationAction } from '@atlassian/rovo-conversation-actions';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { ActionShortcutIconOnly, AIActionShortcut } from '../../../ai-action-shortcut';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { AIButton } from '../../../ai-button';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { AIProactiveNudge } from '../../../ai-proactive-nudge';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { AISmartButtonProps } from '../../../common/types/shared-types';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { isBrowser } from '../../../common/utils/is-browser';

const i18n = defineMessages({
	tooltipLabel: {
		id: 'ai-smart-button.tooltip.label',
		defaultMessage: 'Ask Rovo AI',
		description: 'Label for the AI smart button tooltip',
	},
});

const styles = cssMap({
	buttonWrapper: {
		position: 'relative',
	},
	actionsWrapperStyles: {
		position: 'absolute',
		// @ts-expect-error
		right: '100%',
		marginRight: token('space.100'),
		bottom: token('space.0'),
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-end',
		gap: token('space.050'),
	},
	actionsWrapperIconOnlyStyles: {
		position: 'absolute',
		height: '48px',
		flexShrink: 0,
		// @ts-expect-error
		right: '100%',
		marginRight: token('space.100'),
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.100'),
		backgroundColor: token('color.background.neutral.bold'),
		borderRadius: token('radius.xxlarge'),
		paddingRight: token('space.200'),
		paddingLeft: token('space.200'),
		// @ts-expect-error
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		boxShadow: '0 0 1px 0 rgba(30, 31, 33, 0.31), 0 8px 12px 0 rgba(30, 31, 33, 0.15)',
	},
});

type AISmartButtonUIProps = Pick<
	AISmartButtonProps,
	'product' | 'productPage' | 'sidebarApi' | 'analyticsAttributes' | 'isDisabled' | 'getActions'
> & {
	onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onMouseEnter: () => void;
	onMouseLeave: () => void;
};

export const AISmartButtonUI = forwardRef<HTMLButtonElement, AISmartButtonUIProps>(
	(
		{
			onClick,
			isDisabled,
			onMouseEnter,
			onMouseLeave,
			sidebarApi,
			product,
			productPage,
			analyticsAttributes,
			getActions,
		},
		ref: Ref<HTMLButtonElement>,
	) => {
		const { formatMessage } = useIntl();
		const { toggleConversationAssistantUIMode } = useRovoExperienceAPIStore();

		const [canShowNudge, setCanShowNudge] = useState(false);
		const [spotlightRendered, setSpotlightRendered] = useState(false);
		const [isHovering, setIsHovering] = useState(false);
		const [actionShortcuts, setActionShortcuts] = useState<ConversationAction[]>([]);
		const [hasActiveNudge, setHasActiveNudge] = useState(false);
		const [isFetchingActions, setIsFetchingActions] = useState(false);
		const placementElementRef = useRef<HTMLDivElement | null>(null);
		const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

		// Fetch actions when hovering starts
		useEffect(() => {
			if (
				!spotlightRendered &&
				isHovering &&
				getActions &&
				(FeatureGates.getExperimentValue<string>(
					'confluence_rovo_button_shortcuts_experiment',
					'cohort',
					'control',
				) === 'action-shortcut-icon-only' ||
					FeatureGates.getExperimentValue<string>(
						'confluence_rovo_button_shortcuts_experiment',
						'cohort',
						'control',
					) === 'action-shortcut-icon-and-text')
			) {
				setIsFetchingActions(true);
				getActions()
					.then((fetchedActions) => {
						setActionShortcuts(fetchedActions.slice(0, 3) || []);
					})
					.catch(() => {
						setActionShortcuts([]);
					})
					.finally(() => {
						setIsFetchingActions(false);
					});
			} else if (!isHovering) {
				// Clear actions when not hovering
				setActionShortcuts([]);
				setIsFetchingActions(false);
			}
		}, [spotlightRendered, isHovering, getActions]);

		const onboardingSpotlightElementId = useMemo(() => {
			const isLiveOrEditPage =
				analyticsAttributes?.pageMode === 'live' || analyticsAttributes?.pageMode === 'edit';
			const isPageOrBlogpost = productPage === 'page' || productPage === 'blogpost';

			if (isPageOrBlogpost && isLiveOrEditPage) {
				return `rovo-button-onboarding-spotlight-live-or-edit`;
			}

			return `rovo-button-onboarding-spotlight`;
		}, [productPage, analyticsAttributes]);

		// Handle "Get Started" button click from spotlight
		const handleGetStartedClick = useCallback(() => {
			if (sidebarApi) {
				toggleConversationAssistantUIMode({
					targetMode: 'mini-modal',
					sidebarApi,
				});
			}
		}, [sidebarApi, toggleConversationAssistantUIMode]);

		// Set up event listeners for spotlight communication
		useEffect(() => {
			if (!isBrowser() || !placementElementRef.current || !fg('rovo_button_onboarding_spotlight')) {
				return;
			}

			const doc = getDocument();

			if (!doc) {
				return;
			}

			// Listen for spotlight rendered event
			const handleSpotlightRendered = (event: Event) => {
				const customEvent = event as CustomEvent;
				if (customEvent.detail?.elementId === onboardingSpotlightElementId) {
					setSpotlightRendered(true);
				}
			};

			// Listen for "Get Started" button click event
			const handleGetStartedEvent = (event: Event) => {
				const customEvent = event as CustomEvent;
				if (customEvent.detail?.elementId === onboardingSpotlightElementId) {
					handleGetStartedClick();
				}
			};

			const unbind = bindAll(doc, [
				{
					type: 'rovo-button-onboarding-spotlight-rendered',
					listener: handleSpotlightRendered,
				},
				{
					type: 'rovo-button-onboarding-spotlight-get-started-click',
					listener: handleGetStartedEvent,
				},
			]);

			return () => {
				unbind();
			};
		}, [handleGetStartedClick, onboardingSpotlightElementId]);

		useEffect(() => {
			if (spotlightRendered) {
				return;
			}

			if (!fg('rovo_button_onboarding_spotlight') && !canShowNudge) {
				setCanShowNudge(true);
				return;
			}

			// Give spotlight time to render (if it will) since it needs to run decisions logic
			const timeout = setTimeout(() => {
				if (!spotlightRendered && !canShowNudge) {
					setCanShowNudge(true);
				}
			}, 1000);

			return () => clearTimeout(timeout);
		}, [spotlightRendered, canShowNudge]);

		const nudgeOrPlacement = useMemo(() => {
			return (
				<React.Fragment>
					{fg('rovo_button_onboarding_spotlight') && (
						<div id={onboardingSpotlightElementId} ref={placementElementRef} />
					)}
					{canShowNudge && (
						<AIProactiveNudge
							sidebarApi={sidebarApi}
							product={product}
							onActiveNudgeChange={setHasActiveNudge}
						/>
					)}
				</React.Fragment>
			);
		}, [sidebarApi, product, canShowNudge, onboardingSpotlightElementId]);

		const handleButtonMouseEnter = useCallback(() => {
			setIsHovering(true);
			onMouseEnter();
		}, [onMouseEnter]);

		const handleButtonMouseLeave = useCallback(() => {
			hoverTimeoutRef.current = setTimeout(() => {
				setIsHovering(false);
				onMouseLeave();
			}, 500);
		}, [onMouseLeave]);

		const handleActionBoxMouseEnter = useCallback(() => {
			clearTimeout(hoverTimeoutRef.current);
			setIsHovering(true);
		}, []);

		const handleActionBoxMouseLeave = useCallback(() => {
			setIsHovering(false);
		}, []);

		const actionShortcutsRendering = useMemo(() => {
			if (!isHovering || actionShortcuts.length === 0 || hasActiveNudge || spotlightRendered) {
				return null;
			}

			switch (
				FeatureGates.getExperimentValue<string>(
					'confluence_rovo_button_shortcuts_experiment',
					'cohort',
					'control',
				)
			) {
				case 'action-shortcut-icon-only':
					return (
						<ExitingPersistence appear>
							<FadeIn duration="large" entranceDirection="right">
								{(props) => (
									<div
										ref={props.ref}
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
										className={props.className}
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
										style={props.style}
										css={styles.actionsWrapperIconOnlyStyles}
										onMouseEnter={handleActionBoxMouseEnter}
										onMouseLeave={handleActionBoxMouseLeave}
										onFocus={handleActionBoxMouseEnter}
										onBlur={handleActionBoxMouseLeave}
									>
										{actionShortcuts.map((action) => {
											return (
												<ActionShortcutIconOnly
													key={action.id}
													action={action}
													sidebarApi={sidebarApi}
													product={product}
												/>
											);
										})}
									</div>
								)}
							</FadeIn>
						</ExitingPersistence>
					);
				case 'action-shortcut-icon-and-text':
					return (
						<ExitingPersistence appear>
							<FadeIn duration="large" entranceDirection="right">
								{(props) => (
									<div
										ref={props.ref}
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
										className={props.className}
										// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
										style={props.style}
										css={styles.actionsWrapperStyles}
										onMouseEnter={handleActionBoxMouseEnter}
										onMouseLeave={handleActionBoxMouseLeave}
										onFocus={handleActionBoxMouseEnter}
										onBlur={handleActionBoxMouseLeave}
									>
										{actionShortcuts.map((action) => {
											return (
												<AIActionShortcut
													key={action.id}
													action={action}
													sidebarApi={sidebarApi}
													product={product}
												/>
											);
										})}
									</div>
								)}
							</FadeIn>
						</ExitingPersistence>
					);
				default:
					return null;
			}
		}, [
			isHovering,
			actionShortcuts,
			hasActiveNudge,
			spotlightRendered,
			handleActionBoxMouseEnter,
			handleActionBoxMouseLeave,
			sidebarApi,
			product,
		]);

		return (
			<Inline space="space.100" alignBlock="center" xcss={styles.buttonWrapper}>
				{nudgeOrPlacement}
				{actionShortcutsRendering}
				<Tooltip
					position="left"
					content={formatMessage(i18n.tooltipLabel)}
					hideTooltipOnClick
					ignoreTooltipPointerEvents
					canAppear={() => actionShortcuts.length === 0 && !isFetchingActions}
				>
					<AIButton
						onClick={onClick}
						isDisabled={isDisabled}
						onMouseEnter={handleButtonMouseEnter}
						onMouseLeave={handleButtonMouseLeave}
						ref={ref}
					/>
				</Tooltip>
			</Inline>
		);
	},
);
